import { $getRoot } from "lexical";
import {
  useApiLoader,
  type UseApiLoaderResponse,
} from "../../../../../server/api/hooks/use-api-loader";
import { useToken } from "../../../../../server/auth/use-token";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import type { UUID } from "../../../../../utils/uuid";
import type { ChannelName } from "../../../../channels/types";
import { SupabaseEmailDesignService } from "../../../../email-design/server/supabase-email-design-service";
import { useGetPollsWithOptions } from "../../../../poll/hooks/use-poll-with-options";
import type { SerialisedEditorDesign } from "../../types/serialised-editor-design";
import { calculatePaperSize } from "../convert-to-mjml/calculate-paper-size";
import { $convertToMjmlString } from "../convert-to-mjml/convert-to-mjml";
import { createConfiguredEditor } from "../headless-editor";
import { SlackMrkDwnTransformers } from "../markdown-transformers/slack-transformers";
import { TeamsMrkDwnTransformers } from "../markdown-transformers/teams-transformers";
import { chatBlockJson } from "./chat-block-json";
import { transformTwilio } from "./transform-twilio";

type Config = {
  editorState: SerialisedEditorDesign | null;
  channel: ChannelName;
  format: "email" | "chat" | "sms";
  account_id: UUID;
  email_design_id?: UUID;
  poll_ids?: UUID[];
};

type MessageBodyCreator = (
  config: Config,
) => Promise<
  | { data: { body: string | null; text: string | null }; errorMessage?: undefined }
  | { data?: undefined; errorMessage?: string }
>;

export const useMessageBodyCreator = (): UseApiLoaderResponse<MessageBodyCreator> => {
  const getToken = useToken();

  const getEmailDesign = useSupabaseCallback(
    async ({ supabase, account_id }, email_design_id?: UUID) => {
      const { data: emailDesignData, error } = await new SupabaseEmailDesignService(
        supabase,
      ).maybeGet(email_design_id ?? account_id);

      if (error) {
        throw new Error(`Failed to get email design. ${error?.message ?? ""}`);
      }
      return emailDesignData;
    },
    [],
  );

  const getPolls = useGetPollsWithOptions();

  return useApiLoader(
    // eslint-disable-next-line @typescript-eslint/require-await
    async () => {
      return async ({ editorState, channel, format, account_id, email_design_id, poll_ids }) => {
        if (!editorState || editorState?.type === "markdown") {
          return { body: null, text: null };
        }

        const editor = createConfiguredEditor({
          onError: (error) => {
            console.error("Failed to generate content", error.message);
          },
          format,
        });
        editor.setEditorState(editor.parseEditorState(editorState));

        const pollsData = poll_ids ? await getPolls(poll_ids) : undefined;

        switch (channel) {
          case "work_email":
          case "personal_email": {
            const state = editor.getEditorState();

            const data = await getEmailDesign(email_design_id);

            return {
              data: state.read(() => {
                return {
                  text: $getRoot().getTextContent(),
                  body: $convertToMjmlString(
                    editor,
                    account_id,
                    "paper",
                    calculatePaperSize(data?.layout_settings),
                    pollsData ?? {},
                    data ?? undefined,
                  ),
                };
              }),
            };
          }
          case "slack":
            return {
              data: await chatBlockJson(
                editor,
                SlackMrkDwnTransformers,
                "slack",
                {
                  maxWidth: 600,
                  token: await getToken(),
                },
                account_id,
                pollsData ?? {},
              ),
            };
          case "teams":
            return {
              data: await chatBlockJson(
                editor,
                TeamsMrkDwnTransformers,
                "teams",
                {
                  maxWidth: 580,
                  token: await getToken(),
                },
                account_id,
                pollsData ?? {},
              ),
            };
          case "twilio":
            return { data: transformTwilio(editor) };
          default:
            return { errorMessage: "Channel not supported" };
        }
      };
    },
    [getPolls, getToken, getEmailDesign],
  );
};
